import path from 'ramda/src/path'
import React, { useEffect, useMemo } from 'react'

export interface ITranslationsProviderProps {
  lang: string
  children: any
  translations: Record<string, any>
  formLocale: Record<string, any>
  gridLocale: Record<string, any>
  getTranslations?: (lang: string) => Record<string, any> | undefined
  configFormValidation?: Function
}

export const TranslationContext = React.createContext<{
  lang: string
  translations: Record<string, any>
  gridLocale: Record<string, any>
  getTranslations?: ITranslationsProviderProps['getTranslations']
}>({
  lang: 'EN',
  translations: {},
  gridLocale: {},
})

export const translate = (
  key: string,
  keywords?: string[],
  options?: {
    translations?: Record<string, any>
  }
) => {
  const { translations } = options || {}

  if (!translations) return key
  const value: string | undefined = path(key.split('.'))(translations)

  if (!value) return key

  if (Array.isArray(keywords)) {
    const mutableKeywords = [...keywords]
    const replacedValue = value.replaceAll(
      /{.*?}/g,
      () => mutableKeywords.shift() as string
    )

    return replacedValue
  }

  return value
}

const TranslationsProvider = ({
  lang,
  translations,
  formLocale,
  gridLocale,
  getTranslations,
  children,
  configFormValidation,
}: ITranslationsProviderProps) => {
  const contextValue = useMemo(
    () => ({ lang, translations, gridLocale, getTranslations }),
    [lang, translations, gridLocale, getTranslations]
  )

  useEffect(() => {
    // feed FormValidation with the latest form translations
    configFormValidation?.({
      minReplaceRegexp: /\{min}/g,
      maxReplaceRegexp: /\{max}/g,
      locale: formLocale,
    })
  }, [configFormValidation, formLocale])

  return (
    <TranslationContext.Provider value={contextValue}>
      {children}
    </TranslationContext.Provider>
  )
}

export default TranslationsProvider
